import { compareDesc } from "date-fns/esm";
import { notifyIcon, quote } from "../../../config";
import { getNotification } from "../../../utils/common";
import { WORKFLOW_VARIABLE_LABELS } from "../../Workflow/WorkflowVariables/workflowVariables.constants";

/**
 * Variable validation utilities for form builder
 * @author Abhishek Dhillon
 */

/**
 * Validates variable configuration based on selected variable type
 * @author Abhishek Dhillon
 * @param {Object} variableData - Variable configuration data
 * @param {Function} setHasVarChanges - Function to set variable changes state
 * @returns {boolean} True if validation passes, false if there are errors
 */
export const validateVariableConfiguration = (variableData, setHasVarChanges) => {
  const {
    selectedVariable,
    hasVarChanges,
    variableName,
    isDuplicateName,
    defaultOptions,
    isMaxLength,
    maxLength,
    minLength,
    defaultValue,
    onlyPositiveNumbers,
    startDate,
    endDate,
  } = variableData;

  if (!selectedVariable || !hasVarChanges) {
    return true; // No validation needed if no variable selected or no changes
  }

  const variableType = selectedVariable?.variableType || selectedVariable?.type;

  // Common validation - Variable name
  if (!variableName?.trim()) {
    getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_REQUIRED_ERROR, notifyIcon.WARNING_ICON);
    setHasVarChanges(false);
    return false;
  }

  if (isDuplicateName) {
    getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR, notifyIcon.WARNING_ICON);
    setHasVarChanges(false);
    return false;
  }

  // Type-specific validation
  switch (variableType) {
    case "dropdown":
      return validateDropdownVariable(variableData, setHasVarChanges);
    case "text":
      return validateTextVariable(variableData, setHasVarChanges);
    case "number":
      return validateNumberVariable(variableData, setHasVarChanges);
    case "date":
      return validateDateVariable(variableData, setHasVarChanges);
    default:
      return true;
  }
};

/**
 * Validates dropdown variable configuration
 * @author Abhishek Dhillon
 * @param {Object} variableData - Variable configuration data
 * @param {Function} setHasVarChanges - Function to set variable changes state
 * @returns {boolean} True if validation passes
 */
export const validateDropdownVariable = (variableData, setHasVarChanges) => {
  const { defaultOptions } = variableData;

  if (!defaultOptions?.length) {
    getNotification(WORKFLOW_VARIABLE_LABELS.REQUIRED_OPTIONS, notifyIcon.WARNING_ICON);
    setHasVarChanges(false);
    return false;
  }

  const hasEmptyOption = defaultOptions?.some((option) => option === "" || !option.label);
  if (hasEmptyOption) {
    getNotification(WORKFLOW_VARIABLE_LABELS.EMPTY_OPTION, notifyIcon.WARNING_ICON);
    setHasVarChanges(false);
    return false;
  }

  return true;
};

/**
 * Validates text variable configuration
 * @author Abhishek Dhillon
 * @param {Object} variableData - Variable configuration data
 * @param {Function} setHasVarChanges - Function to set variable changes state
 * @returns {boolean} True if validation passes
 */
export const validateTextVariable = (variableData, setHasVarChanges) => {
  const { isMaxLength, maxLength } = variableData;

  if (isMaxLength && maxLength < 3) {
    getNotification(quote.VARIABLE_MAX_VALUE, notifyIcon.WARNING_ICON);
    setHasVarChanges(false);
    return false;
  }

  return true;
};

/**
 * Validates number variable configuration
 * @author Abhishek Dhillon
 * @param {Object} variableData - Variable configuration data
 * @param {Function} setHasVarChanges - Function to set variable changes state
 * @returns {boolean} True if validation passes
 */
export const validateNumberVariable = (variableData, setHasVarChanges) => {
  const { minLength, maxLength, defaultValue, onlyPositiveNumbers } = variableData;

  // Check if min is greater than max
  if (typeof minLength === "number" && typeof maxLength === "number" && minLength > maxLength) {
    getNotification(WORKFLOW_VARIABLE_LABELS.MIN_MAX_VALUE, notifyIcon.WARNING_ICON);
    setHasVarChanges(false);
    return false;
  }

  // Check default value against min/max constraints
  if (typeof minLength === "number" || typeof maxLength === "number") {
    if (typeof minLength === "number" && defaultValue < minLength) {
      getNotification(WORKFLOW_VARIABLE_LABELS.DEFAULT_VALUE_LESS_THAN_MIN_VALUE, notifyIcon.WARNING_ICON);
      setHasVarChanges(false);
      return false;
    }
    if (typeof maxLength === "number" && defaultValue > maxLength) {
      getNotification(WORKFLOW_VARIABLE_LABELS.DEFAULT_VALUE_GREATER_THAN_MAX_VALUE, notifyIcon.WARNING_ICON);
      setHasVarChanges(false);
      return false;
    }
  }

  // Check positive number constraints
  if (onlyPositiveNumbers) {
    if (defaultValue < 0) {
      getNotification(WORKFLOW_VARIABLE_LABELS.DEFAULT_VALUE_LESS_THAN_ZERO, notifyIcon.WARNING_ICON);
      setHasVarChanges(false);
      return false;
    }
    if (typeof minLength === "number" && minLength < 0) {
      getNotification(WORKFLOW_VARIABLE_LABELS.MIN_VALUE_LESS_THAN_ZERO, notifyIcon.WARNING_ICON);
      setHasVarChanges(false);
      return false;
    }
    if (typeof maxLength === "number" && maxLength < 0) {
      getNotification(WORKFLOW_VARIABLE_LABELS.MAX_VALUE_LESS_THAN_ZERO, notifyIcon.WARNING_ICON);
      setHasVarChanges(false);
      return false;
    }
  }

  return true;
};

/**
 * Validates date variable configuration
 * @author Abhishek Dhillon
 * @param {Object} variableData - Variable configuration data
 * @param {Function} setHasVarChanges - Function to set variable changes state
 * @returns {boolean} True if validation passes
 */
export const validateDateVariable = (variableData, setHasVarChanges) => {
  const { startDate, endDate } = variableData;

  if (startDate && endDate && compareDesc(new Date(startDate), new Date(endDate)) === -1) {
    getNotification(WORKFLOW_VARIABLE_LABELS.START_DATE_GREATER_THAN_END_DATE, notifyIcon.ERROR_ICON);
    setHasVarChanges(false);
    return false;
  }

  return true;
};

/**
 * Creates variable data object from context values
 * @author Abhishek Dhillon
 * @param {Object} contextData - Data from useVariablesContext and useWorkflowContext
 * @returns {Object} Formatted variable data object
 */
export const createVariableDataObject = (contextData) => {
  return {
    selectedVariable: contextData.selectedVariable,
    hasVarChanges: contextData.hasVarChanges,
    variableName: contextData.variableName,
    isDuplicateName: contextData.isDuplicateName,
    defaultOptions: contextData.defaultOptions,
    isMaxLength: contextData.isMaxLength,
    maxLength: contextData.maxLength,
    minLength: contextData.minLength,
    defaultValue: contextData.defaultValue,
    onlyPositiveNumbers: contextData.onlyPositiveNumbers,
    isDecimal: contextData.isDecimal,
    startDate: contextData.startDate,
    endDate: contextData.endDate,
  };
};

/**
 * Validation error types for different scenarios
 */
export const VALIDATION_ERROR_TYPES = {
  VARIABLE_NAME_REQUIRED: "VARIABLE_NAME_REQUIRED",
  VARIABLE_NAME_DUPLICATE: "VARIABLE_NAME_DUPLICATE",
  DROPDOWN_NO_OPTIONS: "DROPDOWN_NO_OPTIONS",
  DROPDOWN_EMPTY_OPTIONS: "DROPDOWN_EMPTY_OPTIONS",
  TEXT_MAX_LENGTH: "TEXT_MAX_LENGTH",
  NUMBER_MIN_MAX_RANGE: "NUMBER_MIN_MAX_RANGE",
  NUMBER_DEFAULT_VALUE: "NUMBER_DEFAULT_VALUE",
  NUMBER_POSITIVE_CONSTRAINT: "NUMBER_POSITIVE_CONSTRAINT",
  DATE_RANGE: "DATE_RANGE",
};

/**
 * Gets validation error message for a specific error type
 * @author Abhishek Dhillon
 * @param {string} errorType - Type of validation error
 * @returns {string} Error message
 */
export const getValidationErrorMessage = (errorType) => {
  const errorMessages = {
    [VALIDATION_ERROR_TYPES.VARIABLE_NAME_REQUIRED]: WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_REQUIRED_ERROR,
    [VALIDATION_ERROR_TYPES.VARIABLE_NAME_DUPLICATE]: WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR,
    [VALIDATION_ERROR_TYPES.DROPDOWN_NO_OPTIONS]: WORKFLOW_VARIABLE_LABELS.REQUIRED_OPTIONS,
    [VALIDATION_ERROR_TYPES.DROPDOWN_EMPTY_OPTIONS]: WORKFLOW_VARIABLE_LABELS.EMPTY_OPTION,
    [VALIDATION_ERROR_TYPES.TEXT_MAX_LENGTH]: quote.VARIABLE_MAX_VALUE,
    [VALIDATION_ERROR_TYPES.NUMBER_MIN_MAX_RANGE]: WORKFLOW_VARIABLE_LABELS.MIN_MAX_VALUE,
    [VALIDATION_ERROR_TYPES.DATE_RANGE]: WORKFLOW_VARIABLE_LABELS.START_DATE_GREATER_THAN_END_DATE,
  };

  return errorMessages[errorType] || "Validation error occurred";
};
